import { type Dispatch } from "@reduxjs/toolkit";
import { err, got, req } from "../slices/threatDefenceSettingsSlice";

import dataService from "features/Application/services/dataServices/typedDataService";

import { sendTransientNotification } from "features/Notifications/state/notificationsActions";
import { AlertTypes } from "enums";
import { type ConsentResult, open } from "features/Accounts/Integrations/connectors/consentWindowService";

export const fetchThreatDefenceSettings = (accountId: number) => async (dispatch: Dispatch) => {
  dispatch(req());
  try {
    const hasMsGraphSettings = await dataService.threatDefence.msGraphSettings.msGraphSettingsExist(accountId);
    const accountDomain = await dataService.threatDefence.domains.getAccountDomainAsync(accountId);

    dispatch(got({ settings: { hasMsGraphSettings }, accountDomain }));
  } catch (e) {
    dispatch(err(new Error(e.message)));
    showTransientMessage("getting", dispatch);
  }
};

export const upsertThreatDefenceSettings = (accountId: number) => async (dispatch: Dispatch) => {
  dispatch(req());
  try {
    await loginAndSaveMsGraphSettings(accountId, dispatch);
  } catch (e) {
    dispatch(err(new Error(e.message)));
    showTransientMessage("saving", dispatch);
  } finally {
    await fetchThreatDefenceSettings(accountId)(dispatch);
  }
};

export const deleteThreatDefenceSettings = (accountId: number) => async (dispatch: Dispatch) => {
  dispatch(req());
  try {
    await dataService.threatDefence.msGraphSettings.deleteMsGraphSettings(accountId);
  } catch (e) {
    dispatch(err(new Error(e.message)));
    showTransientMessage("deleting", dispatch);
  } finally {
    await fetchThreatDefenceSettings(accountId)(dispatch);
  }
};

const showTransientMessage = (action: string, dispatch: Dispatch) =>
  dispatch(
    sendTransientNotification(
      {
        message: `Error ${action} threat defence settings.`,
        type: AlertTypes.error,
      },
      5,
    ),
  );

const loginAndSaveMsGraphSettings = async (accountId: number, dispatch: Dispatch): Promise<boolean> => {
  const authResult = await login();

  if (authResult.isSuccess) {
    await dataService.threatDefence.msGraphSettings.saveMsGraphSettings(authResult.result, accountId);
    dispatch(
      sendTransientNotification(
        {
          title: "Authentication was successful!",
          message: "Microsoft Graph Direct Message is now enabled.",
          type: AlertTypes.success,
        },
        5,
      ),
    );
    return true;
  } else {
    dispatch(
      sendTransientNotification(
        {
          message: "Please try again to enable Microsoft Graph Direct Message.",
          type: AlertTypes.error,
        },
        5,
      ),
    );
    return false;
  }
};

const login = async (): Promise<ConsentResult> => {
  const redirectLocation = await dataService.threatDefence.msGraphSettings.getAuthorizeUrl();
  const redirect = `${window.location.origin}/ms-consent`;
  const fullConsentUri = `${redirectLocation}&redirect_uri=${redirect}`;
  return open({
    location: fullConsentUri,
    title: "Microsoft Graph Sign In",
    parseResult: (params) => {
      const tenantId = params.get("tenant") as string;
      return {
        isSuccess: !!tenantId,
        error: params.get("error"),
        result: tenantId,
        redirect,
      };
    },
  });
};
